/**
 * 全局 Resize 事件管理
 *
 * 功能：
 * 1. 自动监听 window resize，统一触发所有已注册的回调
 * 2. 支持手动调用 triggerGlobalResize() 触发回调（如 JtFormV2 展开/收起时）
 * 3. 注册时立即执行一次回调，确保初始化时布局正确
 *
 * 使用示例：
 *   import { onGlobalResize, triggerGlobalResize } from '@/hooks/useGlobalResize'
 *
 *   // 在需要监听的地方注册回调
 *   const off = onGlobalResize(() => {
 *     tableScroll.y = getTableHeight(400)
 *   })
 *
 *   // 组件卸载时取消订阅
 *   onUnmounted(() => off())
 *
 *   // 在需要强制触发的地方手动调用
 *   triggerGlobalResize()
 */

export type ResizeCallback = () => void | Promise<void>

const listeners = new Set<ResizeCallback>()

let isListening = false

const onResize = () => {
  listeners.forEach((cb) => Promise.resolve(cb()))
}

const startListen = () => {
  if (typeof window === 'undefined' || isListening) return
  window.addEventListener('resize', onResize)
  isListening = true
}

const stopListen = () => {
  if (typeof window === 'undefined' || !isListening) return
  if (listeners.size === 0) {
    window.removeEventListener('resize', onResize)
    isListening = false
  }
}

/**
 * 注册全局 resize 回调
 * @param callback 回调函数
 * @returns 取消订阅函数
 */
export const onGlobalResize = (callback: ResizeCallback): (() => void) => {
  listeners.add(callback)
  startListen()
  // 注册后立即执行一次，保证初始化时布局正确
  callback()

  return () => {
    listeners.delete(callback)
    stopListen()
  }
}

/**
 * 手动触发所有已注册的 resize 回调
 * 适用于非窗口变化但需要重新计算布局的场景（如表单展开/收起）
 */
export const triggerGlobalResize = () => {
  onResize()
}
